import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Save, Plus, Trash2, FileCheck, ChevronUp, ChevronDown } from "lucide-react";

// Starter clauses — admin can tweak after creating. Mirrors common CICC-aligned language.
const STARTERS = {
  SP: [
    { heading: "Scope of Services", body: "The Firm agrees to represent the Client in connection with the preparation and submission of a Study Permit application to IRCC. Services include document review, strategy, submission to IRCC, and reasonable follow-up throughout the process." },
    { heading: "Professional Fees & Disbursements", body: "The Client agrees to pay the professional fee stated on the Fee Agreement page (CAD). Government filing fees, biometric fees, medical exams, translations, and third-party costs are the Client's responsibility and billed separately." },
    { heading: "Client's Responsibilities", body: "The Client agrees to provide complete and truthful information, respond promptly to document requests, and notify the Firm of any change in personal circumstances (address, marital status, dependents) during the engagement." },
    { heading: "Communications", body: "The Firm will communicate by email or phone and will keep the Client informed of material developments. The Client agrees that email is the primary channel of record." },
    { heading: "Termination & Refunds", body: "Either party may terminate this engagement on written notice. Fees are earned as work is performed; any unearned portion will be refunded. Government fees already paid are non-refundable." },
    { heading: "Governing Law", body: "This agreement is governed by the laws of the Province of Ontario and the federal laws of Canada applicable therein." },
  ],
  PR: [
    { heading: "Scope of Services", body: "The Firm agrees to represent the Client in connection with their Permanent Residence application (including Express Entry profile, PR submission, and post-submission follow-up with IRCC)." },
    { heading: "Professional Fees & Disbursements", body: "The professional fee stated on the Fee Agreement is for Firm services only. ECA, language tests, medicals, biometrics, government fees, and translations are the Client's direct responsibility." },
    { heading: "Document Accuracy", body: "The Client confirms that every document, declaration, and statement submitted through the Firm will be true, complete and current. Misrepresentation is a serious offence under the IRPA and may result in a 5-year ban." },
    { heading: "Communications", body: "Updates will be shared by email. The Client will respond within 72 hours to any document request to avoid delays." },
    { heading: "Termination & Refunds", body: "Either party may terminate on written notice. Earned fees are retained; unearned portions are refunded within 30 days." },
    { heading: "Governing Law", body: "Governed by the laws of Ontario and the federal laws of Canada applicable therein." },
  ],
  LMIA: [
    { heading: "Scope of Services", body: "The Firm agrees to prepare and submit an LMIA application on behalf of the Employer Client, including drafting the Transition Plan (if applicable), compiling recruitment proof, and following up with Service Canada." },
    { heading: "Professional Fees & Disbursements", body: "The professional fee is for Firm services only. Government processing fees ($1,000/position), advertising, and job-posting costs are the Employer's direct responsibility." },
    { heading: "Employer Representations", body: "The Employer confirms that the role described is genuine, that recruitment efforts have been (or will be) made in good faith, and that any wage offered meets or exceeds the prevailing wage for the occupation and region." },
    { heading: "Confidentiality", body: "The Firm will treat payroll, revenue, and recruitment data as confidential and use it solely for the purpose of this LMIA application." },
    { heading: "Termination & Refunds", body: "Either party may terminate on written notice. Earned fees are retained; unearned portions are refunded within 30 days." },
    { heading: "Governing Law", body: "Governed by the laws of Ontario and the federal laws of Canada applicable therein." },
  ],
};

export default function ImmigrationRetainerClauses() {
  const [types, setTypes] = useState([]);
  const [sets, setSets] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newHeading, setNewHeading] = useState("");

  const load = async () => {
    const [t, c] = await Promise.all([
      api.get("/immigration/app-types?include_inactive=false"),
      api.get("/immigration/retainer-clauses"),
    ]);
    setTypes(t.data || []); setSets(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const selectedType = useMemo(() => types.find(t => t.id === selectedTypeId), [types, selectedTypeId]);
  const existing = useMemo(() => sets.find(s => s.app_type_id === selectedTypeId), [sets, selectedTypeId]);

  useEffect(() => {
    if (!selectedTypeId) { setDraft([]); return; }
    if (existing) setDraft((existing.clauses || []).map(c => ({ ...c })));
    else {
      const starter = STARTERS[(selectedType?.code || "").toUpperCase()] || [];
      setDraft(starter.map(c => ({ ...c })));
    }
  }, [selectedTypeId, existing, selectedType]);

  const addClause = () => {
    const h = newHeading.trim(); if (!h) return toast.error("Heading required");
    setDraft([...draft, { heading: h, body: "" }]); setNewHeading("");
  };
  const removeAt = (i) => setDraft(draft.filter((_, k) => k !== i));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= draft.length) return;
    const n = [...draft]; [n[i], n[j]] = [n[j], n[i]]; setDraft(n); };
  const upd = (i, key, val) => setDraft(draft.map((c, k) => k === i ? { ...c, [key]: val } : c));

  const save = async () => {
    if (!selectedTypeId) return toast.error("Pick an application type");
    if (draft.length === 0) return toast.error("Add at least one clause");
    for (const c of draft) {
      if (!c.heading?.trim() || !c.body?.trim()) return toast.error("Every clause needs a heading and body");
    }
    setSaving(true);
    try {
      if (existing) await api.patch(`/immigration/retainer-clauses/${existing.id}`, { clauses: draft });
      else await api.post(`/immigration/retainer-clauses`, { app_type_id: selectedTypeId, clauses: draft });
      toast.success("Clauses saved"); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const removeSet = async () => {
    if (!existing) return;
    if (!window.confirm("Delete this clause set? New retainers will fall back to the built-in defaults.")) return;
    try { await api.delete(`/immigration/retainer-clauses/${existing.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const typesWithStatus = types.map(t => ({ ...t, has: sets.some(s => s.app_type_id === t.id) }));

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">Retainer Clauses</h1>
        <p className="text-sm text-slate-500 mt-1">
          Author the standard legal clauses per application type. These clauses are rendered on the retainer PDF
          <strong> only when no custom firm template is uploaded</strong> in <span className="mono">Retainer &amp; RCIC</span> settings — if a template is attached, it takes precedence.
          Each clause supports plain-text paragraphs; line breaks are preserved.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-3 h-fit">
          <div className="text-xs uppercase text-slate-500 px-2 pb-2">Application Types</div>
          <div className="space-y-1">
            {typesWithStatus.length === 0 && <div className="text-sm text-slate-400 p-2">No active application types.</div>}
            {typesWithStatus.map(t => (
              <button
                key={t.id}
                onClick={() => setSelectedTypeId(t.id)}
                data-testid={`clause-type-${t.code || t.id}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 ${
                  selectedTypeId === t.id ? "bg-orange-50 text-orange-800 border border-orange-200" : "hover:bg-slate-50 border border-transparent"
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium truncate">{t.name}</span>
                  <span className="mono text-[10px] text-slate-500">{t.code || "—"}</span>
                </div>
                {t.has ? <FileCheck className="w-4 h-4 text-emerald-600 shrink-0" title="Clauses configured"/> : <span className="text-[10px] text-slate-400 shrink-0">—</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          {!selectedTypeId ? (
            <div className="text-sm text-slate-500 py-10 text-center">Pick an application type on the left to start editing.</div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase text-slate-500">Editing</div>
                  <div className="heading-font text-xl font-semibold">{selectedType?.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Code: {selectedType?.code || "—"} · {existing ? "Editing existing clauses" : "Creating new clause set"}</div>
                </div>
                <div className="flex gap-2">
                  {existing && (
                    <Button variant="outline" onClick={removeSet} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="clause-delete">
                      <Trash2 className="w-4 h-4 mr-1"/>Delete
                    </Button>
                  )}
                  <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="clause-save">
                    <Save className="w-4 h-4 mr-1"/>{saving ? "Saving…" : (existing ? "Save Changes" : "Create Clause Set")}
                  </Button>
                </div>
              </div>

              <div className="border border-dashed border-slate-300 rounded-lg p-3 bg-slate-50 flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-xs text-slate-600">Add Clause (heading)</Label>
                  <Input value={newHeading} onChange={e => setNewHeading(e.target.value)}
                         onKeyDown={e => { if (e.key === "Enter") addClause(); }}
                         placeholder="e.g. Confidentiality"
                         data-testid="clause-new-heading"/>
                </div>
                <Button onClick={addClause} className="bg-slate-900 hover:bg-slate-700 text-white" data-testid="clause-add">
                  <Plus className="w-4 h-4 mr-1"/>Add Clause
                </Button>
              </div>

              <div className="space-y-3">
                {draft.length === 0 && (
                  <div className="rounded-lg bg-slate-50 border border-dashed border-slate-300 p-6 text-sm text-slate-500 text-center">
                    No clauses yet. {selectedType?.code && STARTERS[selectedType.code] ? "A starter list has been loaded — review and Save." : "Add clauses above to get started."}
                  </div>
                )}
                {draft.map((c, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-3 bg-white">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex flex-col mr-1">
                        <button type="button" onClick={() => move(i, -1)} className="text-slate-400 hover:text-slate-700"><ChevronUp className="w-3 h-3"/></button>
                        <button type="button" onClick={() => move(i, +1)} className="text-slate-400 hover:text-slate-700"><ChevronDown className="w-3 h-3"/></button>
                      </div>
                      <div className="text-xs text-slate-400 w-6 text-right">{i + 1}.</div>
                      <Input value={c.heading} onChange={e => upd(i, "heading", e.target.value)} className="font-semibold flex-1" data-testid={`clause-heading-${i}`}/>
                      <Button variant="ghost" size="icon" className="text-rose-600 hover:bg-rose-50" onClick={() => removeAt(i)} data-testid={`clause-remove-${i}`}>
                        <Trash2 className="w-4 h-4"/>
                      </Button>
                    </div>
                    <Textarea value={c.body} onChange={e => upd(i, "body", e.target.value)} rows={4} placeholder="Clause body — plain text, line breaks preserved on PDF." data-testid={`clause-body-${i}`}/>
                    <div className="text-[11px] text-slate-400 mt-1 text-right">{(c.body || "").length} / 4000</div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-slate-500">
                Tip — the retainer PDF always ends with your firm's letterhead, fee breakdown, signatures, audit trail, and the SHA-256 fingerprint.
                These clauses sit between the fee block and the signatures.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
